/*
 * Copyright (C) 2018 DV Bern AG, Switzerland
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    ViewChild,
    inject
} from '@angular/core';
import {NgForm} from '@angular/forms';
import * as Sentry from '@sentry/browser';
import {Category, UIRouter} from '@uirouter/core';
import {visualizer} from '@uirouter/visualizer';
import {Subject} from 'rxjs';
import {distinctUntilChanged, filter, map, takeUntil} from 'rxjs/operators';
import {LogFactory} from '@utils/log';
import {GesuchRS} from '../../../legacy-gesuch/service/gesuchRS.rest';

const LOG = LogFactory.createLog('DebuggingComponent');

@Component({
    selector: 'dv-debugging',
    templateUrl: './debugging.component.html',
    styleUrls: ['./debugging.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DebuggingComponent implements OnInit, OnDestroy {
    private readonly router = inject(UIRouter);
    private readonly gesuchRS = inject(GesuchRS);
    private readonly cd = inject(ChangeDetectorRef);

    @ViewChild('traceForm', {static: true}) private readonly traceForm: NgForm;

    public readonly TRACE_CATEGORY = Category;
    public readonly TRACE_CATEGORY_KEYS = Object.keys(Category).filter(k =>
        isNaN(Number(k))
    ) as (keyof typeof Category)[];

    public routerTraceCategories: Category[];
    public hasVisualizer: boolean;
    public gesuchIdsStr: string;
    public gesuchIds: string[];
    public simulationResult: string = '';
    public simulationsFinished: number = 0;

    private readonly unsubscribe$ = new Subject<void>();

    public constructor() {
        const router = this.router;

        this.routerTraceCategories = this.TRACE_CATEGORY_KEYS.map(
            k => Category[k as any] as any
        ).filter(c => router.trace.enabled(c));

        this.hasVisualizer = !!router.getPlugin('visualizer');
    }

    public ngOnInit(): void {
        this.initTracer();
    }

    public ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    public onActivateVisualizer(): void {
        if (!this.router.getPlugin('visualizer')) {
            this.hasVisualizer = true;
            this.router.plugin(visualizer);
        }
    }

    private initTracer(): void {
        this.traceForm.valueChanges
            .pipe(
                filter(
                    values =>
                        !!values &&
                        values.hasOwnProperty('routerTraceCategories')
                ),
                map(values => values.routerTraceCategories),
                distinctUntilChanged(),
                takeUntil(this.unsubscribe$)
            )
            .subscribe(
                (categories: Category[]) =>
                    categories.forEach(c => this.router.trace.enable(c)),
                err => LOG.error(err)
            );
    }

    public doUndefined(): void {
        const test = [1, 2];
        test[2].toFixed(1);
    }

    public doThrowError(): void {
        throw new Error(
            'This is a delibrate error thrown from an Angular controller'
        );
    }

    public doShowDialog(): void {
        Sentry.showReportDialog();
    }

    public findCategory(key: string): Category {
        switch (key) {
            case 'RESOLVE': {
                return Category.RESOLVE;
            }
            case 'TRANSITION': {
                return Category.TRANSITION;
            }
            case 'HOOK': {
                return Category.HOOK;
            }
            case 'UIVIEW': {
                return Category.UIVIEW;
            }
            default: {
                return Category.VIEWCONFIG;
            }
        }
    }

    public startSimulation(): void {
        this.simulationResult = '';
        this.simulationsFinished = 0;
        this.gesuchIds = this.gesuchIdsStr.split('\n');
        this.gesuchIds.forEach(id => {
            this.gesuchRS.simulateNewVerfuegung(id.trim()).then(res => {
                this.simulationResult += res;
                this.simulationsFinished++;
                this.cd.markForCheck();
            });
        });
    }
}
